import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import useSearchMessages from '@/hooks/search/use-search-messages';
import { parseSessionKey } from '@/utils/session';

export default function useSearchMessagesBySession(sessionKey: string, keyword = '') {
  const [type, id] = parseSessionKey(sessionKey);
  const [searchKeyword, setSearchKeyword] = useState(keyword);
  const { t } = useTranslation();

  const { messages: result, isLoading, clear: clearResults } = useSearchMessages(searchKeyword, { type, id });
  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { keyword },
  });

  const submit = ({ keyword }: FormValues) => {
    if (keyword === searchKeyword) return;
    setSearchKeyword(keyword);
    return keyword;
  };

  const clear = () => {
    setSearchKeyword('');
    clearResults().catch(console.warn);
    form.resetField('keyword', { defaultValue: '' });
  };

  let helperText = '';
  if (form.formState.isSubmitted && result.length === 0) {
    helperText = t('search.no-result');
  } else if (isLoading) {
    helperText = t('search.searching');
  }

  useEffect(() => {
    if (keyword) {
      form.setValue('keyword', keyword);
      submit({ keyword });
    }
  }, [keyword, form.setValue]);

  return {
    form,
    searchKeyword,
    submit,
    clear,
    result,
    isLoading,
    helperText,
  };
}

const schema = z.object({
  keyword: z
    .string()
    .min(1)
    .transform((value) => value.trim()),
});

type FormValues = z.infer<typeof schema>;
